/**
 * src/api/knowledge.ts
 * ----------------------
 * API functions for the Knowledge Base module.
 *
 * Endpoints used:
 *   GET    /knowledge/documents           → paginated document list
 *   POST   /knowledge/documents/upload    → upload file (multipart/form-data)
 *   POST   /knowledge/documents/url       → ingest from URL
 *   GET    /knowledge/documents/:id       → single document detail
 *   DELETE /knowledge/documents/:id       → delete document + chunks
 *   POST   /knowledge/documents/:id/reindex → re-embed a document
 *   GET    /knowledge/documents/:id/status → ingestion status (poll or use SSE)
 *
 *   GET    /knowledge/qa                  → paginated Q&A pairs
 *   POST   /knowledge/qa                  → create Q&A pair
 *   PUT    /knowledge/qa/:id              → update Q&A pair
 *   DELETE /knowledge/qa/:id              → delete Q&A pair
 *   POST   /knowledge/qa/import           → bulk import CSV
 *
 *
 * Depends on: src/api/client.ts, shared/types (KnowledgeDocument, QAPair)
 */
import apiClient from './client'
import type { KnowledgeDocument, QAPair, PaginatedResponse, PaginationParams } from '@shared/types'

export interface UploadDocumentPayload {
  file: File
  name?: string
}

export interface IngestionStatus {
  document_id: string
  status: KnowledgeDocument['status']
  chunks_created: number
  total_chunks: number
  error_message?: string
}


// ── Documents ─────────────────────────────────────────────────────────────────

export async function listDocuments(
  params?: PaginationParams & { status?: KnowledgeDocument['status'] },
): Promise<PaginatedResponse<KnowledgeDocument>> {
  const { data } = await apiClient.get<PaginatedResponse<KnowledgeDocument>>(
    '/knowledge/documents',
    { params },
  )
  return data
}

export async function uploadDocument(payload: UploadDocumentPayload): Promise<KnowledgeDocument> {
  const form = new FormData()
  form.append('file', payload.file)
  if (payload.name) form.append('name', payload.name)
  const { data } = await apiClient.post<KnowledgeDocument>('/knowledge/documents/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function getDocument(id: string): Promise<KnowledgeDocument> {
  const { data } = await apiClient.get<KnowledgeDocument>(`/knowledge/documents/${id}`)
  return data
}

export async function deleteDocument(id: string): Promise<void> {
  await apiClient.delete(`/knowledge/documents/${id}`)
}

export async function getIngestionStatus(id: string): Promise<IngestionStatus> {
  const { data } = await apiClient.get<IngestionStatus>(`/knowledge/documents/${id}/status`)
  return data
}

export interface DocumentChunk {
  id: string
  chunk_index: number
  content: string
  tokens: number
}

export async function getDocumentChunks(
  id: string,
  params?: { page?: number; page_size?: number },
): Promise<{ items: DocumentChunk[]; total: number; page: number; page_size: number }> {
  const { data } = await apiClient.get(`/knowledge/documents/${id}/chunks`, { params })
  return data
}

// ── Q&A Pairs ─────────────────────────────────────────────────────────────────

export async function listQAPairs(params?: PaginationParams): Promise<PaginatedResponse<QAPair>> {
  const { data } = await apiClient.get<PaginatedResponse<QAPair>>('/knowledge/qa', { params })
  return data
}

export async function createQAPair(payload: Omit<QAPair, 'id' | 'org_id' | 'created_at' | 'updated_at'>): Promise<QAPair> {
  const { data } = await apiClient.post<QAPair>('/knowledge/qa', payload)
  return data
}

export async function updateQAPair(id: string, payload: Partial<Pick<QAPair, 'question' | 'answer' | 'tags' | 'guardrails' | 'escalation_note' | 'actions'>>): Promise<QAPair> {
  const { data } = await apiClient.put<QAPair>(`/knowledge/qa/${id}`, payload)
  return data
}

export async function deleteQAPair(id: string): Promise<void> {
  await apiClient.delete(`/knowledge/qa/${id}`)
}

export async function importQAPairsCSV(file: File): Promise<{ imported: number; errors: string[] }> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await apiClient.post<{ imported: number; errors: string[] }>(
    '/knowledge/qa/import',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

// Connected Sources (external connectors) were removed — see ADM-0.5. They were
// stubs that faked connections; re-add with a real connector implementation.
